import type { ApolloError } from "@apollo/client";
import { NetworkStatus, useQuery } from "@apollo/client";
import lightFormat from "date-fns/lightFormat";
import AgendaQuery from "lib/queries/AgendaQuery";
import type { IAgendaQuery, IAgendaQueryVariables } from "lib/queries/__generated__/AgendaQuery.generated";
import type { IIssuerType, IAgendaEventType } from "types/graphqlTypes";
import { IOrderDirection } from "types/graphqlTypes";
import React from "react";
import type { EventsCalenderFilterEventType } from "./enums";
import { EventsCalendarFilterRange } from "./enums";
import { getAgendaEventType, getAgendaIssuerType } from "./utils";

type EventsCalendarChildrenStateProps = {
  data?: IAgendaQuery;
  error?: ApolloError;
  loading: boolean;
  isLoadingMore: boolean;
  loadMoreEvents: () => void;
};

interface EventsCalendarProps {
  myPositions: boolean;
  isInSP500: boolean;
  isInBookmarks: boolean;
  searchQuery: string | null;
  eventType: EventsCalenderFilterEventType | null;
  issuerType: IIssuerType | null;
  period: EventsCalendarFilterRange | null;
  orderBy: IOrderDirection | null;
  calendarInterval: {
    startDate: Date;
    endDate: Date;
  };
  filterByEventTypes?: IAgendaEventType[];
  limit?: number;
  children: (state: EventsCalendarChildrenStateProps) => React.ReactNode;
}

export default function EventsCalendar({
  myPositions,
  isInSP500,
  isInBookmarks,
  searchQuery,
  eventType,
  issuerType,
  period,
  orderBy,
  calendarInterval,
  filterByEventTypes,
  limit = 30,
  children,
}: EventsCalendarProps) {
  const isYearlyFilteringWithoutSearch = period === EventsCalendarFilterRange.InTheYear && !searchQuery;

  const {
    data: currentData,
    previousData,
    loading,
    error,
    fetchMore,
    networkStatus,
  } = useQuery<IAgendaQuery, IAgendaQueryVariables>(AgendaQuery, {
    skip: isYearlyFilteringWithoutSearch,
    notifyOnNetworkStatusChange: true,
    variables: {
      filterByStartDate: lightFormat(calendarInterval.startDate, "yyyy-MM-dd"),
      filterByEndDate: lightFormat(calendarInterval.endDate, "yyyy-MM-dd"),
      filterByEventType: (eventType ? getAgendaEventType(eventType) : null) ?? filterByEventTypes ?? null,
      filterByIssuerType: issuerType ? getAgendaIssuerType(issuerType) || null : null,
      filterByIssuersInPortfolio: myPositions ?? null,
      filterByIssuersInBookmarks: isInBookmarks ?? null,
      filterBySearchQuery: searchQuery ?? null,
      filterByIsInSnP500: isInSP500 ?? null,
      filterByIssuerIds: null,
      direction: orderBy || IOrderDirection.Descending,
      limit,
      cursor: null,
    },
  });

  const data = loading && previousData ? previousData : currentData;

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMoreEvents = () => {
    if (!data) {
      return;
    }

    const { agenda } = data;
    fetchMore({
      variables: {
        cursor: agenda.pageInfo.cursor,
      },
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.agenda) {
          return previousQueryResult;
        }

        return {
          ...previousQueryResult,
          agenda: {
            ...fetchMoreResult.agenda,
            nodes: previousQueryResult.agenda.nodes?.concat(fetchMoreResult.agenda.nodes),
          },
        };
      },
    });
  };

  return (
    <div className="grid w-full grid-cols-12 gap-x-2 gap-y-3 py-4 xl:gap-x-16 xl:px-0">
      {children({ data, error, loading, isLoadingMore, loadMoreEvents })}
    </div>
  );
}
